import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  TUTOR_WEEKDAYS,
  getTutorWeekdayLabel,
  isValidTutorTimezone,
  toHourMinute,
  type TutorWorkingHoursDraft,
  type TutorWorkingHoursFieldErrors,
} from '../../utils/tutorWorkingHours';

function parseTimeValue(value: string): Dayjs | null {
  const hourMinute = toHourMinute(value);
  return hourMinute ? dayjs(`1970-01-01T${hourMinute}`) : null;
}

export interface TutorWorkingHoursEditorProps {
  value: TutorWorkingHoursDraft;
  onChange: (next: TutorWorkingHoursDraft) => void;
  timezone: string;
  onTimezoneChange: (next: string) => void;
  fieldErrors?: TutorWorkingHoursFieldErrors | null;
  showValidation?: boolean;
  disabled?: boolean;
  compact?: boolean;
}

const TutorWorkingHoursEditor: React.FC<TutorWorkingHoursEditorProps> = ({
  value,
  onChange,
  timezone,
  onTimezoneChange,
  fieldErrors,
  showValidation,
  disabled,
  compact = false,
}) => {
  const { t } = useTranslation();

  const updateDay = (
    weekday: (typeof TUTOR_WEEKDAYS)[number],
    patch: Partial<TutorWorkingHoursDraft[(typeof TUTOR_WEEKDAYS)[number]]>
  ) => {
    onChange({
      ...value,
      [weekday]: { ...value[weekday], ...patch },
    });
  };

  const commitDayTime = (
    weekday: (typeof TUTOR_WEEKDAYS)[number],
    field: 'periodStart' | 'periodEnd',
    newValue: Dayjs | null
  ) => {
    if (!newValue?.isValid()) return;
    updateDay(weekday, { [field]: newValue.format('HH:mm') });
  };

  const timezoneInvalid = showValidation && timezone.trim().length > 0 && !isValidTutorTimezone(timezone);

  return (
    <Stack spacing={compact ? 1.25 : 2}>
      <TextField
        fullWidth
        size="small"
        label={t('tutor.workingHours.timezoneLabel', 'Timezone')}
        placeholder="Europe/Warsaw"
        value={timezone}
        disabled={disabled}
        onChange={(e) => onTimezoneChange(e.target.value)}
        error={Boolean(timezoneInvalid)}
        helperText={
          compact
            ? timezoneInvalid
              ? t('tutor.workingHours.timezoneInvalid', 'Enter a valid IANA timezone, e.g. Europe/Warsaw')
              : undefined
            : timezoneInvalid
              ? t('tutor.workingHours.timezoneInvalid', 'Enter a valid IANA timezone, e.g. Europe/Warsaw')
              : t('tutor.workingHours.timezoneHint', 'IANA timezone used for your working hours')
        }
      />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          {TUTOR_WEEKDAYS.map((weekday, index) => {
          const day = value[weekday];
          const dayError = fieldErrors?.[weekday];
          const isClosed = day.dayOff;
          const isOpenAllDay = !day.dayOff && day.openAllDay;
          const showTimes = !isClosed && !isOpenAllDay;
          return (
            <Box
              key={weekday}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: compact
                    ? '80px minmax(0, 1fr) minmax(200px, auto)'
                    : '110px minmax(0, 1fr) minmax(220px, auto)',
                },
                columnGap: compact ? 1 : 2,
                rowGap: 1,
                alignItems: 'center',
                px: compact ? { xs: 1, sm: 1.25 } : { xs: 1.5, md: 2 },
                py: compact ? 0.75 : 1.25,
                borderTop: index === 0 ? 'none' : '1px solid',
                borderColor: 'divider',
                bgcolor: isClosed ? 'action.hover' : 'transparent',
                transition: (theme) => theme.transitions.create('background-color'),
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: compact ? '0.8125rem' : undefined,
                  color: isClosed ? 'text.secondary' : 'text.primary',
                }}
              >
                {getTutorWeekdayLabel(weekday, t)}
              </Typography>

              <Box sx={{ minHeight: compact ? 32 : 40, display: 'flex', alignItems: 'center' }}>
                {showTimes ? (
                  <Stack
                    direction="row"
                    spacing={compact ? 0.75 : 1}
                    alignItems="flex-start"
                    sx={{ width: '100%', maxWidth: compact ? 280 : 360 }}
                  >
                    <TimePicker
                      label={t('tutor.workingHours.start', 'Start')}
                      value={parseTimeValue(day.periodStart)}
                      disabled={disabled}
                      onChange={(newValue) => commitDayTime(weekday, 'periodStart', newValue)}
                      onAccept={(newValue) => commitDayTime(weekday, 'periodStart', newValue)}
                      minutesStep={30}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: Boolean(showValidation && dayError?.periodStart),
                          helperText: showValidation ? dayError?.periodStart : undefined,
                        },
                      }}
                    />
                    <TimePicker
                      label={t('tutor.workingHours.end', 'End')}
                      value={parseTimeValue(day.periodEnd)}
                      disabled={disabled}
                      onChange={(newValue) => commitDayTime(weekday, 'periodEnd', newValue)}
                      onAccept={(newValue) => commitDayTime(weekday, 'periodEnd', newValue)}
                      minutesStep={30}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: Boolean(showValidation && dayError?.periodEnd),
                          helperText: showValidation ? dayError?.periodEnd : undefined,
                        },
                      }}
                    />
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic', fontSize: compact ? '0.8125rem' : undefined }}
                  >
                    {isClosed
                      ? t('tutor.workingHours.closed', 'Closed')
                      : t('tutor.workingHours.open24h', 'Open 24h')}
                  </Typography>
                )}
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                flexWrap="nowrap"
                sx={{
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                  minWidth: 0,
                }}
              >
                <FormControlLabel
                  sx={{
                    mr: 0,
                    ml: 0,
                    flexShrink: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: compact ? '0.8125rem' : undefined,
                      whiteSpace: 'nowrap',
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={day.dayOff}
                      disabled={disabled}
                      onChange={(e) =>
                        updateDay(weekday, {
                          dayOff: e.target.checked,
                          openAllDay: e.target.checked ? false : day.openAllDay,
                        })
                      }
                    />
                  }
                  label={t('tutor.workingHours.dayOff', 'Day off')}
                />
                <FormControlLabel
                  sx={{
                    mr: 0,
                    ml: 0,
                    flexShrink: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: compact ? '0.8125rem' : undefined,
                      whiteSpace: 'nowrap',
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={day.openAllDay}
                      disabled={disabled || day.dayOff}
                      onChange={(e) =>
                        updateDay(weekday, {
                          openAllDay: e.target.checked,
                          dayOff: e.target.checked ? false : day.dayOff,
                        })
                      }
                    />
                  }
                  label={t('tutor.workingHours.open24h', 'Open 24h')}
                />
              </Stack>
            </Box>
          );
          })}
        </Box>
      </LocalizationProvider>
    </Stack>
  );
};

export default TutorWorkingHoursEditor;
